import React, { useState, useRef } from 'react';
import FormCard from 'components/v2/FormCard';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import Select from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import { useTranslation } from 'react-i18next';
import { FileUploader } from 'components/v2/FileUploader';
import validator from 'email-validator';
import { showToast } from 'stores/ToastStore';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import { bulkRegisterUsers } from 'utils/UserWebAPIUtils';
import XLSX from 'xlsx';
import fileDownload from 'react-file-download';
import SessionStore from 'stores/SessionStore';

const items = [
  {
    userType: 'Admin',
    localizationSlug: 'ADMIN',
    fields: [
      '_type',
      'username',
      'email',
      'first_name',
      'last_name',
      'password',
    ],
  },
  {
    userType: 'Driver',
    localizationSlug: 'DRIVER',
    titleize: true,
    fields: [
      '_type',
      'carrier',
      'email',
      'first_name',
      'last_name',
      'phone',
      'password',
      'username',
    ],
  },
  {
    userType: 'Carrier',
    localizationSlug: 'CARRIER',
    titleize: true,
    fields: [
      '_type',
      'company_name',
      'email',
      'dot_number',
      'first_name',
      'last_name',
      'password',
      'phone',
      'phone_extension',
    ],
  },
  {
    userType: 'Shipper',
    localizationSlug: 'SHIPPER',
    titleize: true,
    fields: [
      '_type',
      'company_name',
      'email',
      'sales_rep',
      'first_name',
      'last_name',
      'password',
      'phone',
      'master_account',
    ],
  },
];
const BulkRegisterUsers = () => {
  const { t } = useTranslation();

  const currentUser = SessionStore.getUser();

  const [templateName, setTemplateName] = useState('');
  const [templateFields, setTemplateFields] = useState([]);
  const [emails, setEmails] = useState(currentUser?.email ?? '');
  const fileUploaderRef = useRef(null);
  const [file, setFile] = useState(null);

  const onSelectTemplate = ({ userType, fields }) => {
    setTemplateName(userType);
    setTemplateFields(fields);
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([templateFields]);

    const csv = XLSX.utils.sheet_to_csv(ws);

    fileDownload(csv, `${templateName}.csv`);
  };

  const handleSubmit = async () => {
    if (!emails) {
      showToast('', t('EMAIL_REQUIRED'), 'error');
      return;
    }
    if (isEmailInvalid()) {
      showToast('', t('INVALID_EMAILS'), 'error');
      return;
    }

    if (!file) {
      showToast('', t('CSV_REQUIRED'), 'error');
      return;
    }
    const emailIds = emails.replace(/ /g, '').split(',') || '';
    try {
      await bulkRegisterUsers(file, emailIds);
    } catch (e) {
      console.error(e?.message);
    }
  };

  const isEmailInvalid = () => {
    const emailIds = emails.replace(/ /g, '').split(',') || '';
    if (!emailIds.length || !Array.isArray(emailIds)) {
      return true;
    }

    const invalidEmailAddresses = emailIds.filter(
      (ea) => !validator.validate(ea),
    );

    return invalidEmailAddresses.length > 0;
  };

  const handleFileUpload = (files = []) => {
    if (files?.length) {
      setFile(files[0]);
    } else {
      setFile(null);
    }
  };
  return (
    <FormCard>
      <h4>{t('BULK_REGISTER_USERS')}</h4>
      <p className="text-gray-500 my-2">
        {t('DRAG_AND_DROP_FOR_BULK_REGISTER_USERS')}
      </p>
      <Select
        selected={templateName}
        onSelect={onSelectTemplate}
        caption={t('SELECT_USER')}
        textKey="localizationSlug"
        valueKey="userType"
        items={items}
        className="mt-2"
      />
      {templateName && (
        <button
          className="flex button button-green mt-4"
          onClick={handleDownloadTemplate}
        >
          {t('TEMPLATE')} <DownloadIcon />
        </button>
      )}
      <p className="font-bold mt-2">
        {t('COMMA_SEPARATED_EMAILS_FOR_RESULTS')}
      </p>
      <CargomaticInput
        className="mb-2"
        id="bulk-report-email"
        placeholder={t('EMAIL', { count: 2 })}
        value={emails}
        onChange={(value) => setEmails(value)}
      />
      <FileUploader
        fileTypes={CSV_FILE_TYPES}
        fileTypesLabel={t('CSV')}
        ref={fileUploaderRef}
        onChange={handleFileUpload}
        className="w-16"
      />
      <div className="flex justify-end">
        <button
          className="right-1 mt-4 button button-green"
          onClick={handleSubmit}
        >
          {t('SUBMIT')}
        </button>
      </div>
    </FormCard>
  );
};

export default BulkRegisterUsers;
